import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/** True `delayMs` after `active` became true; false immediately when it turns off. Skeletons after 200 ms, never before. */
export function useDelayed(active: boolean, delayMs = 200): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const t = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(t);
  }, [active, delayMs]);
  return shown;
}

export function useDebounce<T>(value: T, delayMs = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

export function useClickOutside<T extends HTMLElement>(onOutside: () => void) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [onOutside]);
  return ref;
}

export function useLocalStorage<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = useCallback(
    (v: T) => {
      setValue(v);
      try {
        localStorage.setItem(key, JSON.stringify(v));
      } catch {}
    },
    [key],
  );
  return [value, set];
}

/**
 * Dirty registry — every EditableBlock reports whether it holds unsaved edits.
 * The app shell reads `any` to block navigation (react-router useBlocker) and
 * warns on tab close. No business value is ever autosaved.
 */
interface DirtyContextValue {
  register: (id: string, dirty: boolean) => void;
  any: boolean;
  ids: string[];
}
export const DirtyContext = createContext<DirtyContextValue>({ register: () => {}, any: false, ids: [] });

export function useDirtyRegistry(): DirtyContextValue {
  const [map, setMap] = useState<Record<string, boolean>>({});
  const register = useCallback((id: string, dirty: boolean) => {
    setMap((m) => (m[id] === dirty ? m : { ...m, [id]: dirty }));
  }, []);
  const ids = useMemo(() => Object.keys(map).filter((k) => map[k]), [map]);
  const any = ids.length > 0;
  useEffect(() => {
    if (!any) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [any]);
  return { register, any, ids };
}

export function useReportDirty(id: string, dirty: boolean) {
  const { register } = useContext(DirtyContext);
  useEffect(() => {
    register(id, dirty);
    return () => register(id, false);
  }, [id, dirty, register]);
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.replace(/^(rabbi|r\.|mr\.?|mrs\.?)\s+/i, "").split(/\s+/).filter(Boolean);
  return (parts.length >= 2 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : (parts[0] ?? "?").slice(0, 2)).toUpperCase();
}

export function formatDate(d: string | Date | null | undefined, opts: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" }): string {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d.length === 10 ? d + "T00:00:00" : d) : d;
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString(undefined, opts);
}

export function formatDateTime(d: string | Date | null | undefined): string {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function relativeTime(d: string | Date | null | undefined): string {
  if (!d) return "";
  const date = typeof d === "string" ? new Date(d) : d;
  const diff = Date.now() - date.getTime();
  const abs = Math.abs(diff);
  const future = diff < 0;
  const mins = Math.round(abs / 60_000);
  const hours = Math.round(abs / 3_600_000);
  const days = Math.round(abs / 86_400_000);
  if (days >= 30) return formatDate(date);
  const s = mins < 1 ? "just now" : mins < 60 ? `${mins} min` : hours < 24 ? `${hours} h` : `${days} day${days === 1 ? "" : "s"}`;
  if (mins < 1) return s;
  return future ? `in ${s}` : `${s} ago`;
}

export function money(n: number | null | undefined, currency = "USD"): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency, minimumFractionDigits: 2 });
}

export function compact(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : String(n);
}

export function humanize(key: string): string {
  return key.replace(/([A-Z])/g, " $1").replace(/[_-]/g, " ").replace(/\s+/g, " ").trim().replace(/^./, (c) => c.toUpperCase());
}
